import * as Dialog from '@radix-ui/react-dialog'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowRight,
  FileText,
  FolderTree,
  Inbox,
  LayoutDashboard,
  Megaphone,
  Package,
  Search,
  Settings,
  Shield,
  ShoppingCart,
  UserCog,
  Users,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { AdminMenuNode } from '@/api/menu'
import { searchAdminGlobal, type GlobalSearchGroups, type GlobalSearchItem } from '@/api/search'
import { useT } from '@/lib/i18n/AdminI18nProvider'
import { cn } from '@/lib/utils'

const QUICK_LINK_DEFS = [
  { labelKey: 'command.overview', path: '/', icon: LayoutDashboard },
  { labelKey: 'nav.orders', path: '/orders', icon: ShoppingCart },
  { labelKey: 'nav.categories', path: '/categories', icon: FolderTree },
  { labelKey: 'nav.products', path: '/products', icon: Package },
  { labelKey: 'nav.customers', path: '/customers', icon: Users },
  { labelKey: 'common.settings', path: '/settings', icon: Settings },
  { labelKey: 'nav.staff', path: '/staff', icon: UserCog },
  { labelKey: 'nav.inbox', path: '/inbox', icon: Inbox },
  { labelKey: 'nav.permissions', path: '/permissions', icon: Shield },
  { labelKey: 'nav.adverts', path: '/adverts', icon: Megaphone },
  { labelKey: 'command.blog', path: '/blogs', icon: FileText },
] as const

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  menuTree?: AdminMenuNode[]
}

function flattenMenu(nodes: AdminMenuNode[] | undefined, acc: { label: string; path: string }[] = []) {
  for (const node of nodes ?? []) {
    if (node.path) {
      acc.push({ label: node.name, path: node.path })
    }
    if (node.children?.length) {
      flattenMenu(node.children, acc)
    }
  }
  return acc
}

export function CommandPalette({ open, onOpenChange, menuTree }: Props) {
  const t = useT()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)

  const groupLabels = useMemo(
    (): Record<keyof GlobalSearchGroups, string> => ({
      categories: t('nav.categories'),
      products: t('nav.products'),
      orders: t('nav.orders'),
      customers: t('nav.customers'),
      adverts: t('nav.adverts'),
      blogs: t('command.blog'),
    }),
    [t],
  )

  const quickLinks = useMemo(
    () => QUICK_LINK_DEFS.map((l) => ({ ...l, label: t(l.labelKey) })),
    [t],
  )

  const { data: results, isFetching } = useQuery({
    queryKey: ['admin-global-search', query],
    queryFn: () => searchAdminGlobal(query),
    enabled: open && query.trim().length >= 2,
    staleTime: 15_000,
  })

  const menuLinks = useMemo(() => flattenMenu(menuTree), [menuTree])

  const filteredQuick = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return quickLinks
    return quickLinks.filter((l) => l.label.toLowerCase().includes(q))
  }, [query, quickLinks])

  const filteredMenu = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return []
    return menuLinks.filter((l) => l.label.toLowerCase().includes(q)).slice(0, 8)
  }, [menuLinks, query])

  const resultItems = useMemo(() => {
    if (!results || query.trim().length < 2) return [] as { group: string; item: GlobalSearchItem }[]
    const rows: { group: string; item: GlobalSearchItem }[] = []
    ;(Object.keys(groupLabels) as (keyof GlobalSearchGroups)[]).forEach((key) => {
      for (const item of results[key] ?? []) {
        rows.push({ group: groupLabels[key], item })
      }
    })
    return rows
  }, [results, query, groupLabels])

  const allSelectable = useMemo(() => {
    const nav = [
      ...filteredQuick.map((l) => ({ type: 'nav' as const, label: l.label, path: l.path })),
      ...filteredMenu.map((l) => ({ type: 'menu' as const, label: l.label, path: l.path })),
      ...resultItems.map((r) => ({
        type: 'result' as const,
        label: r.item.label,
        path: r.item.path,
        meta: r.group,
      })),
    ]
    return nav
  }, [filteredQuick, filteredMenu, resultItems])

  useEffect(() => {
    setActiveIndex(0)
  }, [query, allSelectable.length])

  const go = useCallback(
    (path: string) => {
      onOpenChange(false)
      setQuery('')
      navigate(path)
    },
    [navigate, onOpenChange],
  )

  useEffect(() => {
    if (!open) {
      setQuery('')
      setActiveIndex(0)
    }
  }, [open])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        onOpenChange(!open)
      }
      if (!open) return
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => Math.min(i + 1, Math.max(allSelectable.length - 1, 0)))
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => Math.max(i - 1, 0))
      }
      if (e.key === 'Enter' && allSelectable[activeIndex]) {
        e.preventDefault()
        go(allSelectable[activeIndex].path)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onOpenChange, allSelectable, activeIndex, go])

  let rowIndex = -1

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-1/2 top-[12%] z-[61] w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
          <Dialog.Title className="sr-only">{t('command.title')}</Dialog.Title>
          <div className="flex items-center gap-2 border-b border-border px-4 py-3">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('command.placeholder')}
              className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
            <kbd className="hidden rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground sm:inline">
              Esc
            </kbd>
            <Dialog.Close className="text-muted-foreground hover:text-foreground" aria-label={t('common.close')}>
              <X className="h-4 w-4" />
            </Dialog.Close>
          </div>

          <div className="max-h-[min(60vh,28rem)] overflow-y-auto p-2">
            {query.trim().length < 2 ? (
              <section className="space-y-1">
                <p className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {t('command.quickJump')}
                </p>
                {filteredQuick.map((link) => {
                  rowIndex += 1
                  const Icon = link.icon
                  const idx = rowIndex
                  return (
                    <button
                      key={link.path}
                      type="button"
                      onClick={() => go(link.path)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors',
                        activeIndex === idx ? 'bg-muted text-foreground' : 'hover:bg-muted/70',
                      )}
                    >
                      <Icon className="h-4 w-4 text-mainColor" />
                      <span className="flex-1 font-medium">{link.label}</span>
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  )
                })}
              </section>
            ) : (
              <>
                {filteredMenu.length > 0 ? (
                  <section className="mb-2 space-y-1">
                    <p className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {t('command.menu')}
                    </p>
                    {filteredMenu.map((link) => {
                      rowIndex += 1
                      const idx = rowIndex
                      return (
                        <button
                          key={`${link.path}-${link.label}`}
                          type="button"
                          onClick={() => go(link.path)}
                          className={cn(
                            'flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm',
                            activeIndex === idx ? 'bg-muted' : 'hover:bg-muted/70',
                          )}
                        >
                          <span className="flex-1">{link.label}</span>
                          <span className="text-xs text-muted-foreground">{link.path}</span>
                        </button>
                      )
                    })}
                  </section>
                ) : null}

                {isFetching ? (
                  <p className="px-3 py-6 text-center text-sm text-muted-foreground">{t('command.searching')}</p>
                ) : resultItems.length > 0 ? (
                  <section className="space-y-1">
                    <p className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {t('command.results')}
                    </p>
                    {resultItems.map(({ group, item }) => {
                      rowIndex += 1
                      const idx = rowIndex
                      return (
                        <button
                          key={`${group}-${item.id}`}
                          type="button"
                          onClick={() => go(item.path)}
                          className={cn(
                            'flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm',
                            activeIndex === idx ? 'bg-muted' : 'hover:bg-muted/70',
                          )}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-medium">{item.label}</p>
                            {item.meta ? (
                              <p className="truncate text-xs text-muted-foreground">{item.meta}</p>
                            ) : null}
                          </div>
                          <span className="shrink-0 text-[10px] uppercase text-muted-foreground">{group}</span>
                        </button>
                      )
                    })}
                  </section>
                ) : filteredMenu.length === 0 ? (
                  <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                    {t('command.empty')}
                  </p>
                ) : null}
              </>
            )}
          </div>

          <div className="border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
            <span className="hidden sm:inline">{t('command.hint')} </span>
            <kbd className="rounded border border-border px-1">Ctrl</kbd>+<kbd className="rounded border border-border px-1">K</kbd>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
